import { PaymentAction } from '@adyen/adyen-web/dist/types/types';
import React from 'react';

import PaymentAuthorizationAction, {
  PaymentAuthorizationActionProps,
} from '@features/ActionCreator/PaymentAuthorizationAction/PaymentAuthorizationAction';
import AdyenManageCreditCards from '@features/ManageSavedCreditCards/AdyenManageCreditCards/AdyenManageCreditCards';
import BancontactAdyenManageCreditCards from '@features/ManageSavedCreditCards/BancontactAdyenManageCreditCards/BancontactAdyenManageCreditCards';
import PaymentMethodSwitch, {
  PaymentMethodSwitchProps,
} from '@features/ManageSavedCreditCards/BraintreeCreditCard/BraintreeManageCreditCards';
import { getSentryService } from '@services/sentryService';
import {
  ContainerType,
  CreditCardData,
  CustomerType,
  Locale,
  PaymentMethod,
  ShopBundle,
} from '@types-internal';
import { getDefaultConfig } from '@utils/configUtils';
import {
  normalizeAdyenManageCreditCardsProps,
  normalizeBancontactAdyenManageCreditCardsProps,
} from '@utils/extractFromHtml';
import { renderInContainer } from '@utils/renderRoot';

const runningEnv = globalThis.window.environment;
const defaultConfig = getDefaultConfig();
const isLocalEnv = runningEnv === 'local';

function log(...values: unknown[]) {
  if (isLocalEnv) {
    console.log.apply(console.log, values);
  }
}

log('Starting app with ENV', process.env);

const base64JsonToJS = <T,>(data?: string) => {
  const jsonAsString = data && data !== '' && Buffer.from(data, 'base64').toString('utf-8');
  return (jsonAsString && (JSON.parse(jsonAsString) as T)) || undefined;
};

const getContainerProps = (
  container: HTMLElement,
  myaccount: boolean,
  registration: boolean,
): PaymentMethodSwitchProps => {
  return {
    shopBundle: container.dataset.shopBundle as ShopBundle,
    domain: container.dataset.domain as string,
    locale: container.dataset.locale?.replace('_', '-') as Locale,
    // Specific below
    errorContainerId: container.dataset.errorContainerId as string,
    currency: container.dataset.currency as string,
    sid: container.dataset.sid as string,
    siteId: container.dataset.siteId ? Number(container.dataset.siteId) : undefined,
    customerId: container.dataset.customerId as string,
    containerType: (myaccount
      ? 'myaccount'
      : registration
        ? 'registration'
        : 'default') as ContainerType,
    customerType: container.dataset.customerType as CustomerType,
    // csrfToken might be undefined for a form not needing API calls (for ex on registration page)
    paymentMethod: container.dataset.paymentMethod as PaymentMethod,
    pspToken: container.dataset.pspToken as string,
    creditCards: base64JsonToJS<CreditCardData[]>(container.dataset.creditCards as string) || [],
    csrfToken: container.dataset.csrfToken,
    isFallbackPsp: container.dataset.fallbackPsp === 'true',
    env: runningEnv,
  };
};

export const renderPaymentSwitch = (force: boolean) => {
  const container = document.getElementById('payment-method-selection') as HTMLElement;

  const myaccount = container?.dataset.myaccount === 'true';
  const registration = container?.dataset.registration === 'true';

  if (container?.dataset.paymentMethod === 'BK') {
    const props = getContainerProps(container, myaccount, registration);

    if (props.errorContainerId) {
      if (isLocalEnv) {
        console.info('BT Render payment-method-selection with', props);
      }

      if (force || !registration) {
        renderInContainer(
          <React.StrictMode>
            <PaymentMethodSwitch {...props} />
          </React.StrictMode>,
          container,
        );
      }
    }
  } else {
    log('BT payment-method-selection container not found');
  }
};

export const renderAdyenManageCreditCards = () => {
  const container = document.getElementById('payment-method-selection');

  if (container?.dataset.paymentMethod === 'AK') {
    const sentryService = getSentryService(runningEnv, defaultConfig);

    try {
      const props = normalizeAdyenManageCreditCardsProps(container, runningEnv);

      log('Render Adyen payment-method-selection with', props);

      renderInContainer(
        <React.StrictMode>
          <AdyenManageCreditCards {...props} />
        </React.StrictMode>,
        container,
      );
    } catch (error) {
      sentryService.captureException({ error });
    }
  } else {
    log('Adyen payment-method-selection container not found');
  }
};

export const renderBancontactAdyenManageCreditCards = () => {
  const container = document.getElementById('bancontact-payment-method-selection');

  if (container?.dataset.paymentMethod === 'BC') {
    const sentryService = getSentryService(runningEnv, defaultConfig);

    try {
      const props = normalizeBancontactAdyenManageCreditCardsProps(container, runningEnv);

      log('Render Bancontact Adyen bancontact-payment-method-selection with', props);

      renderInContainer(
        <React.StrictMode>
          <BancontactAdyenManageCreditCards {...props} />
        </React.StrictMode>,
        container,
      );
    } catch (error) {
      sentryService.captureException({ error });
    }
  } else {
    log('Bancontact Adyen bancontact-payment-method-selection container not found');
  }
};

const renderAuthorizationActionCreator = () => {
  const container = document.getElementById('payment-auth-action-creator-container') as HTMLElement;

  if (container) {
    const props: PaymentAuthorizationActionProps = {
      shopBundle: container.dataset.shopBundle as ShopBundle,
      domain: container.dataset.domain as string,
      locale: container.dataset.locale?.replace('_', '-') as Locale,
      paymentId: container.dataset.paymentId as string,
      paymentMethod: container.dataset.paymentMethod as PaymentMethod,
      csrfToken: container.dataset.csrfToken as string,
      paymentAction: base64JsonToJS(container.dataset.paymentAction) as PaymentAction,
      env: runningEnv,
      customerId: container.dataset.customerId,
      storeCardDetails: container.dataset.storeCardDetails === 'true',
      processPaymentDetailsUrl: container.dataset.termUrl ?? '/',
      isMobileApp: container.dataset.mobileApp === 'true',
      sid: container.dataset.sid,
      currency: container.dataset.currency,
      siteId: container.dataset.siteId ? Number(container.dataset.siteId) : undefined,
      customerType: container.dataset.customerType,
    };

    renderInContainer(
      <React.StrictMode>
        <PaymentAuthorizationAction {...props} />
      </React.StrictMode>,
      container,
    );
  } else {
    log('payment-auth-action-creator container not found');
  }
};

renderPaymentSwitch(false);

// Adyen Authorizer: Bancontact QR Code and credit card 3DS
renderAuthorizationActionCreator();

globalThis.window.Payment = {
  ...globalThis.window.Payment,
  braintreeRenderPaymentSwitch: () => renderPaymentSwitch(true),
  renderAdyenManageCreditCards,
  renderBancontactAdyenManageCreditCards,
};
